import {IconButton} from '@dropbox/dig-components/buttons';
import {LayerContext} from '@dropbox/dig-components/layer';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Box, Split} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CloseLine, NewChatLine} from '@dropbox/dig-icons/assets';
import {
  type AiSearchAskSource,
  bucketDrawerWidth,
  endAiSearchSession,
  logAiSearchDrawerViewed,
  logAiSearchInteraction,
} from 'analytics/aiSearchAnalytics';
import {
  AI_DRAWER_MIN_WIDTH_PX,
  aiDrawerViewAtom,
  aiSearchComposerDraftAtom,
  aiSearchConversationIdAtom,
  aiSearchDrawerWidthAtom,
  aiSearchIsStreamingAtom,
  aiSearchPendingKnowledgePageIdAtom,
  aiSearchPendingMentionsAtom,
  aiSearchPendingProfileAskAtom,
  aiSearchPendingQuestionAtom,
  aiSearchPendingSourceAtom,
  aiSearchQueuedMessageAtom,
  aiSearchTurnsAtom,
} from 'atoms/aiSearch';
import {pulseUserAtom} from 'atoms/auth';
import {growthbookCacheAtom} from 'atoms/growthbook';
import {isMobileAtom} from 'atoms/layout';
import {snackbarAtom} from 'atoms/snackbar';
import {AiQueryConversationTurn} from 'components/ai/aiQueryConversation';
import {
  AiSearchAttachButton,
  useAiSearchAttachInterest,
} from 'components/ai/AiSearchAttachInterest';
import {AiSearchConnectedSources} from 'components/ai/AiSearchConnectedSources';
import {AiSearchEmptyState, AskSuggestionChips} from 'components/ai/AiSearchEmptyState';
import {AiSearchResizeHandle} from 'components/ai/AiSearchResizeHandle';
import {QueuedMessageBanner} from 'components/ai/QueuedMessageBanner';
import {takeSpeculativeRun} from 'components/ai/speculativeAsk';
import {useAiQueryConversation} from 'components/ai/useAiQueryConversation';
import {useAskLandingSpace} from 'components/ai/useAskLandingSpace';
import {ChatComposer, ChatComposerHandle} from 'components/DSYS/chat/ChatComposer';
import {ChatDropTarget} from 'components/DSYS/chat/ChatDropTarget';
import {useMentionMemory} from 'components/DSYS/chat/chatMentions';
import {ChatThread} from 'components/DSYS/chat/ChatThread';
import {CompanyAskGlyph} from 'components/DSYS/CompanyOSLogo';
import {DSYS_MAIN_OFFSET_LEFT} from 'components/DSYS/layoutConstants';
import {Title} from 'components/DSYS/Title';
import {queryShape} from 'helpers/queryShape';
import {isSuperUser} from 'helpers/utils';
import {t} from 'i18next';
import {useAtom, useAtomValue, useSetAtom} from 'jotai';
import {type CSSProperties, useCallback, useEffect, useMemo, useRef, useState} from 'react';

import styles from './AiSearchDrawer.module.css';

const NAVBAR_HEIGHT_PX = 52;
/** Space kept clear on the left so a very wide drawer never covers the nav rail. */
const AI_DRAWER_LEFT_GUTTER_PX = 72;
/**
 * Ask sits at the same z-index as page-level right drawers (comments/privacy,
 * `Layout` z-index 10) but is rendered *after* the page in `LoggedInPage`, so at
 * an equal z-index it paints on top of them via DOM order. Critically this keeps
 * Ask *below* the global header (z-index 11) so the header's account/notification
 * dropdowns still layer above it. Do not raise this above the header.
 */
const AI_SEARCH_DRAWER_Z_INDEX = 10;
/** DIG tooltips above drawer chrome (drawer z-index 10). */
const AI_SEARCH_TOOLTIP_LAYER_Z_INDEX = 20;

export const AiSearchDrawer = () => {
  const isMobile = useAtomValue(isMobileAtom);
  const pulseUser = useAtomValue(pulseUserAtom);
  const setSnackbar = useSetAtom(snackbarAtom);
  const [view, setView] = useAtom(aiDrawerViewAtom);
  const [storedWidth, setStoredWidth] = useAtom(aiSearchDrawerWidthAtom);
  const [turns, setTurns] = useAtom(aiSearchTurnsAtom);
  const [conversationId, setConversationId] = useAtom(aiSearchConversationIdAtom);
  const [isStreaming, setIsStreaming] = useAtom(aiSearchIsStreamingAtom);
  const [question, setQuestion] = useAtom(aiSearchComposerDraftAtom);
  const [pendingQuestion, setPendingQuestion] = useAtom(aiSearchPendingQuestionAtom);
  const [pendingSource, setPendingSource] = useAtom(aiSearchPendingSourceAtom);
  const [pendingKnowledgePageId, setPendingKnowledgePageId] = useAtom(
    aiSearchPendingKnowledgePageIdAtom
  );
  const [pendingMentions, setPendingMentions] = useAtom(aiSearchPendingMentionsAtom);
  const [pendingProfileAsk, setPendingProfileAsk] = useAtom(aiSearchPendingProfileAskAtom);
  const [queuedMessage, setQueuedMessage] = useAtom(aiSearchQueuedMessageAtom);

  const elevated = pulseUser ? isSuperUser(pulseUser) : false;
  const growthbook = useAtomValue(growthbookCacheAtom);
  const enableSourcegraph = growthbook.isSourcegraphEnabled;

  const composerRef = useRef<ChatComposerHandle>(null);
  const mentionMemory = useMentionMemory();
  const onAttachInterest = useAiSearchAttachInterest();

  const {ask, stop, reset} = useAiQueryConversation({
    elevated,
    enableSourcegraph,
    turns,
    setTurns,
    conversationId,
    setConversationId,
    isStreaming,
    setIsStreaming,
  });

  const hasTurns = turns.length > 0;
  const isVisible = view !== 'closed';
  const isExpanded = view === 'expanded';
  // A question handed over from search is about to start, so don't lift the
  // composer only to drop it again.
  const isLanding =
    growthbook.isAskRelaunchEnabled && !hasTurns && !isStreaming && !pendingQuestion;
  const landingSpace = useAskLandingSpace({enabled: isExpanded && isLanding});

  const logAsk = useCallback(
    (q: string, source: AiSearchAskSource, turnIndex: number, opts?: {fresh?: boolean}) => {
      logAiSearchInteraction({
        action: 'ask',
        source,
        turnIndex,
        queryLength: q.length,
        queryShape: queryShape(q),
        conversationId: opts?.fresh ? undefined : (conversationId ?? undefined),
      });
      // Queued and edited messages are plain text by now; the memory still
      // knows who each @Name in them is.
      void ask(q, {...opts, mentions: mentionMemory.resolve(q)});
    },
    [ask, conversationId, mentionMemory]
  );

  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === 'undefined' ? 0 : window.innerWidth
  );
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const maxWidth = Math.max(AI_DRAWER_MIN_WIDTH_PX, viewportWidth - AI_DRAWER_LEFT_GUTTER_PX);
  const drawerWidth = Math.min(maxWidth, Math.max(AI_DRAWER_MIN_WIDTH_PX, storedWidth));

  const streamSignal = useMemo(
    () =>
      turns
        .map((turn) => `${turn.assistant.answer.length}:${turn.assistant.trace.length}`)
        .join('|') + (isStreaming ? '*' : ''),
    [turns, isStreaming]
  );

  useEffect(() => {
    if (!isExpanded) {
      return;
    }
    logAiSearchDrawerViewed();
  }, [isExpanded]);

  useEffect(() => {
    if (!pendingQuestion) {
      return;
    }
    const q = pendingQuestion;
    const source = pendingSource ?? 'drawer_composer';
    const knowledgePageId = pendingKnowledgePageId ?? undefined;
    const mentions = pendingMentions ?? undefined;
    const profileAsk = pendingProfileAsk ?? undefined;
    setPendingQuestion(null);
    setPendingSource(null);
    setPendingKnowledgePageId(null);
    setPendingMentions(null);
    setPendingProfileAsk(null);
    // Search may have started this turn while it was typed.
    const speculativeRun = takeSpeculativeRun(q);
    logAiSearchInteraction({
      action: 'drawer_open',
      source: 'search_ask',
    });
    logAiSearchInteraction({
      action: 'ask',
      source,
      turnIndex: 0,
      queryLength: q.length,
      queryShape: queryShape(q),
      speculative: Boolean(speculativeRun),
      speculativeLeadMs: speculativeRun ? Date.now() - speculativeRun.startedAt : undefined,
    });
    void ask(q, {fresh: true, speculativeRun, knowledgePageId, mentions, profileAsk});
  }, [
    ask,
    pendingQuestion,
    pendingSource,
    pendingKnowledgePageId,
    pendingMentions,
    pendingProfileAsk,
    setPendingQuestion,
    setPendingSource,
    setPendingKnowledgePageId,
    setPendingMentions,
    setPendingProfileAsk,
  ]);

  useEffect(() => {
    if (!isExpanded) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) {
        return;
      }
      event.preventDefault();
      minimizeDrawer();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  const minimizeDrawer = useCallback(() => {
    endAiSearchSession('minimize');
    setView('minimized');
  }, [setView]);

  const handleStop = useCallback(() => {
    logAiSearchInteraction({action: 'stop', turnIndex: Math.max(0, turns.length - 1)});
    stop();
  }, [stop, turns.length]);

  const onNewChat = useCallback(() => {
    endAiSearchSession('new_chat', true);
    logAiSearchInteraction({action: 'new_chat', turnCount: turns.length});
    reset();
    setQuestion('');
    setQueuedMessage(null);
    mentionMemory.forget();
    requestAnimationFrame(() => composerRef.current?.focus());
  }, [mentionMemory, reset, setQuestion, setQueuedMessage, turns.length]);

  // Auto-send the queued message when streaming finishes.
  const wasStreamingRef = useRef(isStreaming);
  useEffect(() => {
    const wasStreaming = wasStreamingRef.current;
    wasStreamingRef.current = isStreaming;
    if (wasStreaming && !isStreaming && queuedMessage) {
      const q = queuedMessage;
      setQueuedMessage(null);
      logAiSearchInteraction({action: 'queue_message', operation: 'auto_send'});
      logAsk(q, 'drawer_composer', turns.length);
    }
  }, [isStreaming, queuedMessage, setQueuedMessage, logAsk, turns.length]);

  const handleDeleteQueued = useCallback(() => {
    setQueuedMessage(null);
    logAiSearchInteraction({action: 'queue_message', operation: 'delete'});
  }, [setQueuedMessage]);

  const handleEditQueued = useCallback(() => {
    if (queuedMessage) {
      setQuestion(queuedMessage);
      setQueuedMessage(null);
      logAiSearchInteraction({action: 'queue_message', operation: 'edit'});
      requestAnimationFrame(() => composerRef.current?.focus());
    }
  }, [queuedMessage, setQuestion, setQueuedMessage]);

  const handleSendNowQueued = useCallback(() => {
    if (queuedMessage) {
      logAiSearchInteraction({action: 'queue_message', operation: 'send_now'});
      stop();
    }
  }, [queuedMessage, stop]);

  if (!isVisible) {
    return null;
  }

  const onCopy = (text: string, message = 'Copied.') => {
    void navigator.clipboard.writeText(text);
    setSnackbar({text: message});
  };

  if (!isExpanded) {
    return null;
  }

  const shellStyle: CSSProperties = {
    top: NAVBAR_HEIGHT_PX,
    right: 0,
    height: `calc(100vh - ${NAVBAR_HEIGHT_PX}px)`,
    zIndex: AI_SEARCH_DRAWER_Z_INDEX,
    boxShadow: 'var(--dig-utilities__boxshadow__raised)',
  };

  if (isMobile) {
    shellStyle.left = DSYS_MAIN_OFFSET_LEFT;
    shellStyle.width = `calc(100% - ${DSYS_MAIN_OFFSET_LEFT})`;
  } else {
    shellStyle.width = drawerWidth;
  }

  const askSuggestion = ({question: q}: {question: string}) => {
    setQuestion('');
    logAsk(q, 'drawer_composer', 0);
  };

  const chatThread = (
    <ChatThread
      hasMessages={hasTurns}
      isStreaming={isStreaming}
      contentVersion={streamSignal}
      backgroundColor="Background Base"
      paddingX="16"
      composerClassName={styles.readingColumn}
      composer={
        <Box ref={landingSpace.composerRef}>
          {queuedMessage && (
            <Box paddingX="16" className={styles.readingColumn}>
              <QueuedMessageBanner
                message={queuedMessage}
                onDelete={handleDeleteQueued}
                onEdit={handleEditQueued}
                onSendNow={handleSendNowQueued}
              />
            </Box>
          )}
          <ChatComposer
            ref={composerRef}
            value={question}
            onChange={setQuestion}
            mentions
            onMentionsChange={mentionMemory.remember}
            onSubmit={() => {
              const q = question.trim();
              if (!q) {
                return;
              }
              setQuestion('');
              if (isStreaming) {
                setQueuedMessage(q);
                logAiSearchInteraction({action: 'queue_message', operation: 'queue'});
                return;
              }
              logAsk(q, 'drawer_composer', turns.length);
            }}
            onStop={handleStop}
            actions={
              <LayerContext.Provider value={{zIndex: AI_SEARCH_TOOLTIP_LAYER_Z_INDEX}}>
                <AiSearchAttachButton onClick={() => onAttachInterest('button')} />
              </LayerContext.Provider>
            }
            onPasteFiles={(files) => onAttachInterest('paste', files)}
            isStreaming={isStreaming}
            canSubmitWhileStreaming={!queuedMessage}
            placeholder={hasTurns ? t('ai_search_follow_up') : t('ai_search_placeholder')}
            submitLabel={t('ai_search_ask')}
            minHeight={64}
            textSize="medium"
          />
          {/* Inside the measured composer, so the landing space makes room for them. */}
          {isLanding && <AskSuggestionChips onAsk={askSuggestion} />}
        </Box>
      }
    >
      {!hasTurns && !isStreaming ? (
        <Box ref={landingSpace.heroRef}>
          <AiSearchEmptyState redesign={growthbook.isAskRelaunchEnabled} onAsk={askSuggestion} />
        </Box>
      ) : (
        turns.map((turn) => (
          <Box key={turn.id} paddingBottom="12">
            <AiQueryConversationTurn
              turn={turn}
              onCopy={onCopy}
              enableAnalytics
              showSaveLiveView={pulseUser?.email?.startsWith('kshay@')}
              answerContentClassName={styles.answerContent}
              frameClassName={styles.readingColumn}
            />
          </Box>
        ))
      )}
    </ChatThread>
  );

  const minimizeLabel = t('ai_search_minimize_tooltip');
  const newChatTooltip = t('ai_search_new_chat_tooltip');

  return (
    <Box
      position="fixed"
      backgroundColor="Background Base"
      borderLeft="Solid"
      borderColor="Border Subtle"
      display="flex"
      flexDirection="column"
      aria-label={t('ai_search_chat_label')}
      role="complementary"
      className={growthbook.isAskRelaunchEnabled ? styles.shellEnter : undefined}
      style={shellStyle}
    >
      {!isMobile ? (
        <AiSearchResizeHandle
          width={drawerWidth}
          minWidth={AI_DRAWER_MIN_WIDTH_PX}
          maxWidth={maxWidth}
          onWidthChange={setStoredWidth}
          onResizeEnd={(width) =>
            logAiSearchInteraction({
              action: 'resize',
              widthBucket: bucketDrawerWidth(width, maxWidth),
            })
          }
        />
      ) : null}
      <Box
        // The relaunch drops the rule under the header, with the connector pills.
        borderBottom={growthbook.isAskRelaunchEnabled ? undefined : 'Solid'}
        borderColor="Border Subtle"
        paddingY="12"
        paddingX="16"
        style={{flexShrink: 0}}
      >
        <Box width="100%">
          <Split alignY="center" gap="8">
            <Split.Item width="fill">
              <Split alignY="center" gap="8">
                <Split.Item>
                  <UIIcon src={CompanyAskGlyph} />
                </Split.Item>
                <Split.Item>
                  <Title size={16}>{t('ai_search_chat_label')}</Title>
                </Split.Item>
                {!growthbook.isAskRelaunchEnabled && (
                  <Split.Item>
                    <AiSearchConnectedSources tooltipZIndex={AI_SEARCH_TOOLTIP_LAYER_Z_INDEX} />
                  </Split.Item>
                )}
              </Split>
            </Split.Item>
            <Split.Item width="hug">
              <Split alignY="center" gap="4">
                <LayerContext.Provider value={{zIndex: AI_SEARCH_TOOLTIP_LAYER_Z_INDEX}}>
                  <Split.Item>
                    <Tooltip
                      title={newChatTooltip}
                      placement="bottom"
                      openDelay={300}
                      shouldCloseOnClick
                    >
                      <IconButton
                        variant="borderless"
                        onClick={onNewChat}
                        aria-label={t('ai_search_new_chat')}
                      >
                        <UIIcon src={NewChatLine} />
                      </IconButton>
                    </Tooltip>
                  </Split.Item>
                  <Split.Item>
                    <Tooltip
                      title={minimizeLabel}
                      placement="bottom"
                      openDelay={300}
                      shouldCloseOnClick
                    >
                      <IconButton
                        variant="borderless"
                        onClick={minimizeDrawer}
                        aria-label={minimizeLabel}
                      >
                        <UIIcon src={CloseLine} />
                      </IconButton>
                    </Tooltip>
                  </Split.Item>
                </LayerContext.Provider>
              </Split>
            </Split.Item>
          </Split>
        </Box>
      </Box>

      <ChatDropTarget
        label={t('ai_search_drop_hint')}
        onDropFiles={(files) => onAttachInterest('drop', files)}
      >
        {growthbook.isAskRelaunchEnabled ? (
          <Box
            ref={landingSpace.paneRef}
            display="flex"
            flexDirection="column"
            flexGrow={1}
            minHeight="0"
          >
            {chatThread}
            <Box
              aria-hidden
              className={`${styles.landingSpace}${isLanding ? ` ${styles.landingSpaceOpen}` : ''}`}
              style={{height: landingSpace.height}}
            />
          </Box>
        ) : (
          chatThread
        )}
      </ChatDropTarget>
    </Box>
  );
};
